import axios, { AxiosInstance, ResponseType } from "axios"
import { useAuthStore, useCustomerStore, useSystemAndContextStore } from "@/stores"
// @ts-ignore
import _ from "lodash"
import { useLoading } from "vue-loading-overlay"
import { CONSTANT } from "@/helpers/constant"
import { logoutFromBrowser } from "@/helpers/common"
import { SessionRefreshOutcome } from "@/types/session"

// A session recovery fires its own requests — `session/refresh`, then
// `fetchCustomerInfo` / `fetchCurrentContext` inside it. Those carry a fresh,
// unmarked config, so the per-config retry marker cannot stop them: a 403 from any
// of them would start yet another recovery, unbounded. This guard is module level
// on purpose — several HttpService instances exist, but the session is one thing.
let sessionRecovery: Promise<SessionRefreshOutcome> | null = null

// Counts the recoveries currently running. Every request created while one is in
// flight is marked with `__eecomSkipSessionRecovery` in the request interceptor, which
// is how a recovery's own calls opt out of waiting for the promise they are part of.
// Recognising them by URL alone is not enough: the moment one of those endpoints is
// built differently (path parameter, absolute URL, no leading slash) or the recovery
// starts calling a fourth one, the match misses and that request waits for its own
// promise — a silent, permanent hang. A counter rather than a boolean because the
// recovery timeout below can release `sessionRecovery` while the old attempt still runs.
let recoveryInProgress = 0

/**
 * Request config for a call that must never start a session recovery, passed as the `extra`
 * argument of `post`. Same marker the request interceptor sets during a recovery. The refresh
 * token mint at app start needs it: it holds the cross-tab session lock, and a recovery started
 * by its 403 would wait for that lock until the recovery timeout logged the user out.
 */
export const WITHOUT_SESSION_RECOVERY = { __eecomSkipSessionRecovery: true }

// Second line of defence behind the marker above, for a recovery request that somehow
// reaches the handler unmarked. Matched exactly (a prefix check would swallow
// `/eecom-teamportal/context-token` along with `/context`). Only consulted while a
// recovery is actually in flight — see `isUnmarkedRecoveryRequest`.
const SESSION_RECOVERY_ROUTES: string[] = [CONSTANT.API.SESSION_REFRESH, CONSTANT.API.ACCOUNT_CUSTOMER, CONSTANT.API.SYSTEM_CONTEXT.FETCH_CONTEXT]

const isSessionRecoveryRoute = (url: string): boolean => SESSION_RECOVERY_ROUTES.some((route) => url === route || url.startsWith(`${route}?`))

/**
 * The URL match alone would exclude `/account/customer` and `/context` from recovery
 * *for the whole page life*: the app calls both of them itself (app start, switching
 * views), and with an expired token from localStorage those are typically the very
 * first requests. Rejecting them without recovery leaves the portal half loaded with
 * no redirect to the login — so the fallback only applies while a recovery is really
 * running. `recoveryInProgress` is the authoritative window; `sessionRecovery` is
 * checked as well because the recovery timeout can release the shared promise while
 * the counter is still raised, and vice versa.
 */
const isUnmarkedRecoveryRequest = (url: string): boolean => (recoveryInProgress > 0 || sessionRecovery !== null) && isSessionRecoveryRoute(url)

// Last resort: however the two guards above are circumvented, nobody waits on the
// shared promise longer than this. A hung recovery then ends the session instead of
// leaving every pending request without a logout, an error or a timeout.
const SESSION_RECOVERY_TIMEOUT_MS = 15000

let sessionLogoutIssued = false

/**
 * `logoutFromBrowser` pushes to the login route and reloads. Several requests can
 * reach that point together, so it runs once per page life.
 */
const logoutOnce = async (): Promise<void> => {
  if (sessionLogoutIssued) {
    return
  }
  sessionLogoutIssued = true
  await logoutFromBrowser()
}

/**
 * Rebuilds the session from the stored refresh token. Runs once for however many requests
 * fail with a lost session at the same time — they all await the same promise — and logs
 * out exactly once if it cannot be recovered.
 *
 * BEHAVIOUR CHANGE (PORTAL-6084): this used to re-login with the customer id held in the
 * customer store — `loginWithCustomerId(customer.id)`. That endpoint logs in *whoever* the
 * id names, so a teamportal sub-user whose context had expired came back as the club
 * owner, with the owner's rights and none of the sub-user's permission checks. A customer
 * id identifies an account; it must not be able to open one. The refresh token can: it is
 * minted only for a session someone signed into, is single use, and the backend revokes it
 * on any password change, deactivation or logout.
 *
 * No token stored (an older browser session, a login from before this ticket, or a token
 * already spent) means there is nothing to recover from, and the user signs in again. That
 * is the honest outcome — the previous fallback only looked better than it was.
 *
 * The outcome is passed through rather than reduced to a boolean because a restored session
 * is not automatically the right session to replay into: see `identity-changed` below.
 */
const performSessionRecovery = async (): Promise<SessionRefreshOutcome> => {
  const authStore = useAuthStore()

  // `refreshSession` reports its outcome rather than throwing, but a bug inside it must not
  // leave requests hanging on a rejected promise, so the call stays guarded.
  let outcome: SessionRefreshOutcome = "failed"
  try {
    outcome = await authStore.refreshSession()
  } catch (refreshError) {
    outcome = "failed"
  }

  if (outcome === "failed") {
    await logoutOnce()
  }

  if (outcome === "identity-changed") {
    // The restored session is another account — in practice the rep's own, after their view of
    // a customer expired — so nothing on the current page belongs to it. The refresh reads the
    // contact back without navigating (report v3, RISK-01), so the move happens here: once per
    // recovery however many requests share it, and after the session lock is released. Still
    // inside the recovery window on purpose: the route guard fetches `/context`, and that request
    // has to be marked as part of the recovery instead of waiting on the promise it belongs to.
    try {
      await useCustomerStore().goToDashboard()
    } catch (navigationError) {
      console.error(navigationError)
    }
  }

  return outcome
}

/**
 * Wraps the recovery so every request it fires itself can be recognised by the request
 * interceptor, regardless of which URL it ends up using. The window has to span the
 * whole recovery, not just the refresh call, because `refreshSession` continues with
 * `fetchCustomerInfo` and `fetchCurrentContext` before it returns, and an identity change
 * ends with a navigation whose route guard fires requests of its own.
 */
const recoverSession = async (): Promise<SessionRefreshOutcome> => {
  recoveryInProgress += 1
  try {
    return await performSessionRecovery()
  } finally {
    recoveryInProgress -= 1
  }
}

/** `"timeout"` when the recovery neither succeeded nor failed in time. */
type SessionRecoveryOutcome = SessionRefreshOutcome | "timeout"

const awaitSessionRecovery = (recovery: Promise<SessionRefreshOutcome>): Promise<SessionRecoveryOutcome> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  return Promise.race<SessionRecoveryOutcome>([
    recovery,
    new Promise<SessionRecoveryOutcome>((resolve) => {
      timer = setTimeout(() => resolve("timeout"), SESSION_RECOVERY_TIMEOUT_MS)
    }),
  ]).finally(() => {
    if (timer !== undefined) {
      clearTimeout(timer)
    }
  })
}

// Ways the Store API tells us the sw-context-token is no longer valid. Kept in one
// place so a newly discovered signal only has to be added here — the recovery path
// (re-login, then logout) keys off this list alone.
//
// BEHAVIOUR CHANGE (PORTAL-6269), deliberate and app-wide: before this ticket *every*
// 403 ended in `logoutFromBrowser()` — localStorage cleared, redirect to login, full
// page reload — and then resolved with `undefined`. Now only a 403 carrying one of the
// codes below does that; every other 403 is rejected and reaches the calling store.
// That is the fix for place-offer-order (its business rejections made the "Transmit To
// ERP" button look like a logout), but it applies to all routes, not just that one.
//
// Two consequences worth knowing before extending this:
//   * ~96% of the store actions wrap their HttpService call in try/catch, so they do
//     get the error — but most catch blocks only `console.error` the `detail` and set
//     no user-visible message. A 403 that used to be a loud (if wrong) logout is now
//     silent. The handful of actions that pass a rejection through uncaught is listed
//     in the PR description; there is no global `unhandledrejection`/`errorHandler`
//     hook in `main.ts`, so for those the only trace is browser console noise.
//   * This list is the *whole* dead-token contract now. `CHECKOUT__CUSTOMER_NOT_LOGGED_IN`
//     is the only session-invalid code this frontend knows of (it is also the only
//     entry in `CONSTANT.ERROR.CODE` besides a password-violation code), so nothing
//     else could be added here honestly. OPEN POINT for the backend: if any further
//     403 code means "context token is dead" — an expired sales-rep context token is
//     the suspected case, and place-offer-order's own 403 set is defined backend-side
//     and not enumerated here — it has to be added, otherwise it no longer reaches
//     recovery or logout. The warning in the handler below exists to surface exactly
//     those codes from a real session instead of guessing at them.
export const SESSION_LOST_ERROR_CODES: string[] = [CONSTANT.ERROR.CODE.CHECKOUT__CUSTOMER_NOT_LOGGED_IN]

/**
 * Marks a path segment that carries data rather than route structure: a run of eight or
 * more hex characters (a Shopware id is 32 of them; a dashed UUID still has an eight-char
 * chunk), a run of four or more digits (customer number, order number) or an `@`.
 * Deliberately a little greedy — no `g` flag, `test` has to stay stateless. Redacting one
 * route segment too many costs nothing, the diagnosis only needs the shape of the route.
 */
const IDENTIFIER_SEGMENT = /[0-9a-f]{8,}|\d{4,}|@/i

/**
 * The URL of a passed-through 403 is logged to find session codes this frontend does not
 * know yet (see above). Store API routes interpolate `{customerId}`, `{offerId}`, media
 * ids and so on, and query strings carry search terms — none of that belongs in a console
 * line that ends up in screenshots and support tickets. So: query and fragment dropped
 * entirely, data-looking path segments replaced by `:id`.
 */
const redactUrlForLog = (url: string): string => {
  const path = url.split(/[?#]/)[0]
  return path.replace(/[^/]+/g, (segment) => (IDENTIFIER_SEGMENT.test(segment) ? ":id" : segment))
}

export class HttpService {
  private axios: AxiosInstance
  private isReqInProgress: boolean = false

  constructor(useLoader: boolean = true) {
    let serviceCount = 0
    const $loading = useLoading()
    let loader: any
    this.axios = axios.create({
      baseURL: process.env.VUE_APP_SW_URL,
      timeout: parseInt(process.env.VUE_APP_HTTP_CLIENT_TIMEOUT || "30000"),
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
        "sw-access-key": process.env.VUE_APP_SW_ACCESS_KEY,
      },
    })
    // Set default headers for all axios requests
    axios.defaults.headers.common["sw-access-key"] = process.env.VUE_APP_SW_ACCESS_KEY

    // Add a request interceptor
    this.axios.interceptors.request.use(
      async (config) => {
        // Anything fired while a recovery runs is part of that recovery (or at least
        // cannot be rescued by it — its token is being replaced right now), so it must
        // not wait on the recovery promise. Marking the config here covers every call
        // the recovery makes, including ones added later and ones whose URL is built
        // dynamically; the response handler only has to read the flag.
        if (recoveryInProgress > 0) {
          const recoveryConfig = config as any
          recoveryConfig.__eecomSkipSessionRecovery = true
        }
        // console.log("serviceCount",serviceCount);
        // serviceCount++;
        // console.log("serviceCount:after",serviceCount);
        // if(serviceCount > 0) loader = $loading.show();
        // Do something before request is sent
        if (!this.isReqInProgress) {
          this.isReqInProgress = true
          loader = useLoader && $loading.show()
        }
        // Set the sw-access-key header if it is not already set
        if (config.headers && !config.headers["sw-access-key"]) {
          config.headers["sw-access-key"] = process.env.VUE_APP_SW_ACCESS_KEY
        }
        // `eecom-teamportal/session/` joins this list because a refresh runs with a context
        // token that is already dead — and sometimes with none at all, when storage was
        // cleaned up — and the store-api still wants the header. Same handling as the login
        // routes: send what we have, fall back to the access key.
        if (
          config.url?.includes("login") ||
          config.url?.includes("account/recovery-password") ||
          config.url?.includes("eecom-teamportal/context-token") ||
          config.url?.includes("eecom-teamportal/session/")
        ) {
          const authStore = useAuthStore()
          const contextToken = authStore.contextToken
          if (contextToken) {
            // @ts-ignore
            config.headers["sw-context-token"] = contextToken
          } else {
            // @ts-ignore
            config.headers["sw-context-token"] = process.env.VUE_APP_SW_ACCESS_KEY
          }
        } else {
          try {
            const authStore = useAuthStore()
            const customerStore = useCustomerStore()
            const systemAndContextStore = useSystemAndContextStore()
            const languageList = systemAndContextStore.getLanguages
            const defaultLanguage = systemAndContextStore.getDefaultLanguage
            let langId = localStorage.getItem("teamportal-lang-id") as string

            if (!langId || langId === "undefined") {
              // @ts-ignore
              langId = _.get(defaultLanguage, "id", "")
            }

            if (langId) {
              // @ts-ignore
              config.headers["sw-language-id"] = langId
            }
            // if (customerStore.customer && customerStore.customer.languageId) {
            //     // @ts-ignore
            //     config.headers["sw-language-id"] = customerStore.customer.languageId;
            // }
            // JC V2 filter/configurator backend routes require the Sales Rep context
            // (Customer.customFields.eecom_tp_is_sales_rep). When a Sales Rep impersonates
            // a customer, the regular contextToken belongs to that customer and lacks the
            // flag — fall back to the stored salesRepContextToken for these specific routes.
            // Important: do NOT match the older /backend/jersey/* and /backend/product/* routes,
            // they rely on the impersonated customer context (basic-info save, sponsors, etc.).
            const url = config.url ?? ""
            const isJcV2Route =
              url.includes("/eecom-jersey-configurator/backend/filter-group") ||
              url.includes("/eecom-jersey-configurator/backend/filter-option") ||
              url.includes("/eecom-jersey-configurator/backend/configurator/") ||
              url.includes("/eecom-jersey-configurator/backend/assortment-product/") ||
              url.includes("/eecom-jersey-configurator/config/") ||
              url.includes("/eecom-jersey-configurator/sponsor/media/") ||
              url.includes("/eecom-jersey-configurator/embed/")
            const contextToken = isJcV2Route && authStore.salesRepContextToken ? authStore.salesRepContextToken : authStore.contextToken
            if (contextToken) {
              // @ts-ignore
              config.headers["sw-context-token"] = contextToken
            }
          } catch (e: any) {
            console.error(e.message)
          }
        }
        // Specific logic for GraphQL requests
        if (config.url?.includes("/graphql")) {
          config.withCredentials = true; // Send cookies with GraphQL requests
        }
        return config
      },
      (error) => {
        this.loaderHide(loader)
        console.debug(error) // for debug
        return Promise.reject(error)
      }
    )

    // Add a response interceptor
    this.axios.interceptors.response.use(
      (response) => {
        // if(serviceCount > 0) {
        //     serviceCount--;
        // }
        // if(serviceCount === 0){
        //     this.loaderHide(loader);
        // }
        this.loaderHide(loader)
        /*
              this doesn't worke because shopware does not save the new contextToken if token is expired

                        const authStore = useAuthStore();


                        const contextToken = _.get(response.headers,'sw-context-token',null);
                        console.log(contextToken);
                        if ( contextToken && contextToken !== 'undefined' && contextToken !== authStore.contextToken ){
                            authStore.contextToken = contextToken;
                            localStorage.setItem('contextToken', contextToken);
                        }

             */
        // Do something with response data
        return response
      },
      async (error) => {
        this.loaderHide(loader)

        // Axios only parses the body when the response type is the default one. A
        // blob request (the offer PDF download) hands us a Blob even for an error,
        // so read it back first — otherwise every failure looks like "no error at
        // all" and the caller silently shows nothing.
        const rawData = _.get(error, "response.data", undefined)
        if (typeof Blob !== "undefined" && rawData instanceof Blob) {
          try {
            error.response.data = JSON.parse(await rawData.text())
          } catch (e) {
            // Not JSON — leave the blob untouched.
          }
        }

        const errorObj = _.get(error, "response.data.errors[0]", undefined)
        // Trust the HTTP status over the body: error payloads do not always carry a
        // status field, and network/timeout failures carry no body at all.
        // Compare numerically. `response.status` is a number, the JSON:API body carries
        // a string, and CONSTANT.ERROR.ERROR_CODE is a string today — a type mismatch
        // on either side would silently disable the whole session-recovery path, and no
        // happy-path test would notice.
        const httpStatus = Number(_.get(error, "response.status", NaN))
        const bodyStatus = Number(_.get(errorObj, "status", NaN))
        const status = Number.isNaN(httpStatus) ? bodyStatus : httpStatus
        const code = _.get(errorObj, "code", undefined)
        const requestUrl: string = _.get(error, "config.url", "") || ""
        // Jersey-Configurator V2 filter/configurator routes legitimately return 403
        // for Sales-Rep-only CRUD when called with an impersonated customer context.
        // Bubble the error to the calling store instead of logging the user out.
        // Scope must match the V2-specific paths only — the older /backend/jersey/* and
        // /backend/product/* routes (basic info, sponsors, ...) must keep their existing
        // 403 handling, otherwise real auth issues would be silently swallowed.
        const isJcFilterRoute =
          requestUrl.includes("/eecom-jersey-configurator/backend/filter-group") ||
          requestUrl.includes("/eecom-jersey-configurator/backend/filter-option") ||
          requestUrl.includes("/eecom-jersey-configurator/backend/configurator/") ||
          requestUrl.includes("/eecom-jersey-configurator/backend/assortment-product/") ||
          requestUrl.includes("/eecom-jersey-configurator/config/") ||
          requestUrl.includes("/eecom-jersey-configurator/sponsor/media/") ||
          requestUrl.includes("/eecom-jersey-configurator/embed/")

        // Only a genuinely lost session may end in a logout. Every other 403 is a
        // permission or business rejection — place-offer-order alone has six of them
        // (offer not owned by the customer, customer not assigned to the rep, sales
        // rep session mismatch, ...). Those must reach the calling store so it can
        // show a message; logging the user out made every one of them look like the
        // session had expired.
        const isSessionLost = SESSION_LOST_ERROR_CODES.includes(code)

        // Deliberately unchanged: a 401 used to fall out of this handler without a
        // return, which resolves the promise with `undefined`. Call sites depend on
        // that (they test `result?.status` and never catch), so turning it into a
        // rejection here would break them. Auditing every caller is out of scope.
        // Matched on the body status exactly like before, so the set of requests
        // taking this path does not widen either.
        // NOTE: a 401 therefore still cleans up nothing — the stale token stays in
        // storage. That was already true before this change (the logout call was
        // commented out); turning it into a real logout is a product decision, not a
        // side effect of this fix.
        if (bodyStatus === Number(CONSTANT.ERROR.ERROR_CODE.UNAUTHORIZED)) {
          return undefined
        }

        if (status === Number(CONSTANT.ERROR.ERROR_CODE.FORBIDDEN) && isSessionLost && !isJcFilterRoute) {
          const config: any = error.config ?? {}

          // A view that mounts fires several calls at once, so several of them can fail
          // with the same dead token. They share one recovery instead of each opening
          // their own, and each is replayed afterwards — rejecting them outright left
          // the view empty even though the session had just been restored.
          //
          // The recovery's own calls opt out: they cannot wait on the promise they are
          // part of. `__eecomSkipSessionRecovery` is set by the request interceptor for
          // everything issued during a recovery and is the reliable signal; the route
          // list stays as a fallback, but only while a recovery is in flight — outside
          // of one, `/account/customer` and `/context` are ordinary app requests and
          // have to be able to trigger a re-login like any other. Deliberately not
          // rejecting *every* session-lost error while `recoveryInProgress > 0`:
          // requests that failed before the recovery started are exactly the ones that
          // should share and be replayed.
          if (!error.config || config.__eecomSkipSessionRecovery === true || isUnmarkedRecoveryRequest(requestUrl)) {
            return Promise.reject(error)
          }

          // Already replayed once after a re-login that reported success, and refused
          // the same way again — the session really is unusable, so end it.
          if (config.__eecomSessionRetried === true) {
            await logoutOnce()
            return Promise.reject(error)
          }

          let recovery = sessionRecovery
          if (!recovery) {
            const started: Promise<SessionRefreshOutcome> = recoverSession().finally(() => {
              // Only clear our own attempt — a timeout may have replaced it already.
              if (sessionRecovery === started) {
                sessionRecovery = null
              }
            })
            sessionRecovery = started
            recovery = started
          }

          const outcome = await awaitSessionRecovery(recovery)

          if (outcome === "timeout") {
            // The recovery is stuck. Release the promise so a later failure can start a
            // fresh attempt, and end the session: waiting on forever is worse than a
            // logout, because it leaves the user with a frozen view and no message.
            if (sessionRecovery === recovery) {
              sessionRecovery = null
            }
            await logoutOnce()
            return Promise.reject(error)
          }

          if (outcome === "failed") {
            // `recoverSession` has already logged out.
            return Promise.reject(error)
          }

          if (outcome === "unavailable") {
            // The backend gave no verdict on the token (offline, or a 5xx during a deploy) and
            // the store has put it back. Only this request fails. The shared promise is released,
            // so the next lost-session 403 starts a fresh attempt and the session survives a
            // short outage (report v3, RISK-03). Unlike "timeout", which is a recovery that hung
            // and ends the session.
            return Promise.reject(error)
          }

          if (outcome === "identity-changed") {
            // There is a session again, but it belongs to a different account: the rep's
            // own. An expired impersonation can only ever be recovered that way — the
            // impersonated session is refused a refresh token, so the one in storage is the
            // rep's (PORTAL-6084 review, RISK-01). Replaying would send a request aimed at
            // the customer — a read of their data, or worse an order or an address write —
            // into the rep's own account, under the rep's own rights, with nothing in the
            // response to show it went somewhere else. So the failure goes to the caller.
            // `refreshSession` has already ended the impersonation and `performSessionRecovery`
            // has sent the rep to their own dashboard, so the view this request was made for
            // is gone anyway.
            return Promise.reject(error)
          }

          config.__eecomSessionRetried = true

          // `error.config` still carries the headers of the first attempt, dead
          // `sw-context-token` included. The request interceptor above does overwrite that
          // header on every request, so the replay would pick up the fresh token anyway —
          // but the replay must not depend on that staying true: the moment the interceptor
          // only fills the header in when it is missing, every replay fails again and the
          // marker just above turns that into a logout, which is the exact symptom this
          // ticket removes. So set it here as well, explicitly.
          //
          // `contextToken` is the right token here, not `salesRepContextToken`: this branch
          // is only reached for `!isJcFilterRoute`, and those JC-V2 routes are the only ones
          // that run on the sales-rep context. Left untouched when the store has no token,
          // so the interceptor's `sw-access-key` fallback for the login routes still applies.
          const recoveredContextToken = useAuthStore().contextToken
          if (recoveredContextToken) {
            config.headers = { ...(config.headers ?? {}), "sw-context-token": recoveredContextToken }
          }

          return await this.axios.request(config)
        }

        // A 403 that is handed to the caller instead of ending the session — the change
        // described at SESSION_LOST_ERROR_CODES. Usually correct: it is a business or
        // permission rejection and the store shows the reason. But a context token that
        // died and is reported with a code we do not know yet is indistinguishable from
        // it here, and would now silently skip recovery *and* logout. Logging the code
        // is what makes that case findable in a real session, so the list can be closed
        // against the backend instead of guessed at.
        //
        // `warn`, not `error`: the common case is not a defect. JC-V2 routes are excluded
        // because they answer 403 routinely by design (Sales-Rep-only CRUD under an
        // impersonated context) and would drown the signal.
        if (status === Number(CONSTANT.ERROR.ERROR_CODE.FORBIDDEN) && !isSessionLost && !isJcFilterRoute) {
          const safeUrl = redactUrlForLog(requestUrl)
          console.warn(`403 passed to the caller without logout (code: ${code ?? "none"}, url: ${safeUrl}). If this code means the session is dead, add it to SESSION_LOST_ERROR_CODES.`)
        }

        return Promise.reject(error)
      }
    )
  }

  public get<T>(url: string, params?: any, responseType?: ResponseType | undefined): Promise<T> {
    return this.axios.get(url, { 
      params,
      responseType: responseType ?? undefined,
    })
  }

  public post<T>(url: string, data?: any, extra?: any): Promise<T> {
    return this.axios.post(url, data, extra)
  }

  public patch<T>(url: string, data?: any): Promise<T> {
    return this.axios.patch(url, data)
  }

  public put<T>(url: string, data?: any): Promise<T> {
    return this.axios.put(url, data)
  }

  public delete<T>(url: string, params?: any): Promise<T> {
    return this.axios.delete(url, { params })
  }

  public graphql<T>(endpoint: string, query: string, variables?: Record<string, any>, customerId?: string): Promise<T> {
    return this.axios.post(endpoint, {
      query,
      variables,
      id: customerId
    });
  }
  private loaderHide = (loader: any) => {
    if (this.isReqInProgress && loader) {
      this.isReqInProgress = false
      loader.hide()
    }
  }
}
